import type { Maybe } from '@soundxyz/utils';
import { CHANNELS_ERROR_ACTIONS, ERROR_TYPE, PILLARS } from '@soundxyz/utils/constants/logging';
import { useAuth } from '@context/auth/base';
import { triggerLogin } from '@context/auth/store';
import { useToast } from '@context/toast';
import { RefetchOnComplete } from '@graphql/helpers/effects';
import type { CurrentUserChannelJoinStatus } from '@graphql/reactQuery';
import {
  ChannelCuratorsDocument,
  ChannelMembersDocument,
  ChannelStatsDocument,
  gql,
  JoinOrLeaveChannelDocument,
  useMutation,
} from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogErrorNew';
import { useStableCallback } from '@hooks/useStableCallback';
import { trackClickEvent } from '@lib/analytics';

/*#__PURE__*/ gql(/* GraphQL */ `
  mutation JoinOrLeaveChannel($channelId: UUID!, $join: Boolean!) {
    setUserJoinStatusOnChannel(channelId: $channelId, join: $join) {
      __typename
      ... on Error {
        message
      }
    }
  }
`);

RefetchOnComplete({
  trigger: [JoinOrLeaveChannelDocument],
  refetch: [ChannelStatsDocument, ChannelMembersDocument, ChannelCuratorsDocument],
});

export function useJoinOrLeaveChannel({
  channelId,
  joinStatus,
}: {
  channelId: string;
  joinStatus: Maybe<CurrentUserChannelJoinStatus>;
}) {
  const { authUser } = useAuth();
  const logError = useLogError();
  const { openToast } = useToast();

  const { mutateAsync: joinOrLeave, isLoading } = useMutation(JoinOrLeaveChannelDocument, {
    retry: 5,
  });

  const joinOrLeaveChannel = useStableCallback(async () => {
    if (!authUser) {
      triggerLogin();
      return;
    }

    try {
      const result = await joinOrLeave({
        channelId,
        join: joinStatus === 'JOINED' ? false : true,
      });

      trackClickEvent({
        event: joinStatus === 'JOINED' ? 'User left channel' : 'User joined channel',
        properties: {
          associationType: 'Channel',
          associationId: channelId,
          channelId,
        },
      });

      if (
        result.data.setUserJoinStatusOnChannel.__typename ===
        'MutationSetUserJoinStatusOnChannelSuccess'
      ) {
        openToast({
          text: `You've ${joinStatus === 'JOINED' ? 'leaved' : 'joined'} the channel`,
          variant: 'success',
        });
        return;
      }

      if (result.data.setUserJoinStatusOnChannel.__typename === 'NotFoundError') {
        throw new Error('The channel does not exist.');
      }
    } catch (error) {
      const message = 'Failed to join/leave channel';
      logError({
        action: CHANNELS_ERROR_ACTIONS.CHANNEL_SETTINGS_SAVE_FAILED,
        error,
        errorType: ERROR_TYPE.CHANNELS,
        message,
        toast: message,
        level: 'error',
        isArtistOnlyPage: false,
        pillar: PILLARS.CHANNELS,
      });
    }
  });

  return { joinOrLeaveChannel, isLoading };
}
